import { createFileRoute, Link } from "@tanstack/react-router";

import { fitTitle, fitDescription } from "@/lib/seo-text";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { FIRM, FIRM_ADDRESS, faqSchema } from "@/lib/firm";
import { ServiceReading } from "@/components/service-reading";

const URL = "https://coastsidelaw.com.au/wills-estate-lawyers-sunshine-coast";

const FAQS = [
  {
    q: "Should I review my will when buying or downsizing on the Sunshine Coast?",
    a: "It is a useful time to review it, especially if ownership, family circumstances or the people you want to appoint have changed. Check how the new home is held, your superannuation nominations and any existing powers of attorney. A property purchase does not automatically mean your will needs replacing; the documents and circumstances need checking together.",
  },
  {
    q: "Does our jointly owned home pass under our wills?",
    a: "It depends on the ownership. A joint tenant's interest generally passes to the surviving joint tenant by survivorship, outside the will. A tenants-in-common share is generally dealt with through the estate. Check the title rather than relying on how you describe the home, and remember other assets may have different arrangements.",
  },
  {
    q: "How much does a will cost on the Sunshine Coast?",
    a: "At Coastside Law a simple will is from $660 including GST, and an enduring power of attorney is from $440 including GST. A will with a testamentary trust starts at $2,200 including GST. Wills involving a blended family, a business or a trust are quoted in writing before we start.",
  },
  {
    q: "Do I need probate to administer a deceased estate?",
    a: "Not every estate needs a grant. Check how assets are owned and what each bank, share registry or other asset holder requires; there is no single threshold for every institution. Joint-tenancy property generally passes by survivorship, but other assets may still need a grant. Probate and full estate administration are different services.",
  },
  {
    q: "How long does probate take in Queensland?",
    a: "Timing depends on the documents, notice requirements, registry workload and any requisitions or disputes. The notice period is only one stage, not a promised grant date. Collecting assets, paying debts and administering the estate are further work; see our Queensland application guide for the steps.",
  },
  {
    q: "What does an executor have to do?",
    a: "Find the will, secure the assets, check whether a grant is needed, deal with debts and tax, and keep proper accounts before distributing. Passing six months does not automatically make distribution safe: claims, notices, tax and executor duties still need checking. Get advice before paying beneficiaries.",
  },
  {
    q: "Can you come to my home or hospital?",
    a: "Yes. We take will and power of attorney instructions at our Golden Beach office, at home, in hospital or in a retirement village unit, and by phone or video.",
  },
  {
    q: "Which Sunshine Coast suburbs do you cover?",
    a: "Our office is at Golden Beach and we regularly act for clients in Caloundra, Pelican Waters, Baringa, Aura, Currimundi, Little Mountain and across the Sunshine Coast. Estate administration can be handled remotely wherever the family lives.",
  },
];

export const Route = createFileRoute("/wills-estate-lawyers-sunshine-coast")({
  head: () => ({
    meta: [
      { title: fitTitle("Wills Sunshine Coast | From $660 | Coastside Law") },
      {
        name: "description",
        content: fitDescription(
          "Sunshine Coast wills lawyers in Golden Beach near Caloundra. Wills from $660 and EPOAs from $440 incl. GST. Local appointments and home visits.",
        ),
      },
      { property: "og:title", content: "Wills & Estate Lawyers Sunshine Coast | Coastside Law" },
      {
        property: "og:description",
        content: fitDescription(
          "Wills from $660 and EPOAs from $440 including GST. Estate planning for Sunshine Coast families from our Golden Beach office near Caloundra.",
        ),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LegalService",
          name: `${FIRM.name} — wills and estate lawyers, Sunshine Coast`,
          url: URL,
          telephone: FIRM.phone,
          address: {
            "@type": "PostalAddress",
            streetAddress: FIRM.street,
            addressLocality: FIRM.suburb,
            addressRegion: FIRM.state,
            postalCode: FIRM.postcode,
            addressCountry: "AU",
          },
          areaServed: ["Sunshine Coast", "Caloundra", "Golden Beach", "Pelican Waters", "Baringa", "Aura", "Currimundi", "Little Mountain"].map(
            (n) => ({ "@type": "Place", name: `${n}, Queensland` }),
          ),
          serviceType: ["Wills", "Enduring powers of attorney", "Estate planning", "Probate", "Letters of administration", "Deceased estate administration"],
        }),
      },
    ],
  }),
  component: Page,
});

const SERVICES = [
  { t: "Wills", d: "Solicitor-drafted wills from $660, including advice on how joint property, super and life insurance actually pass." },
  { t: "Enduring powers of attorney", d: "From $440. Choose who makes financial and health decisions if you can't." },
  { t: "Estate planning", d: "Testamentary trusts, blended families, business interests and SMSFs, quoted in writing before we start." },
  { t: "Probate and letters of administration", d: "Supreme Court of Queensland applications, notices and Public Trustee service, with or without a will." },
  { t: "Deceased estate administration", d: "Dealing with banks, super funds and insurers, selling the home, paying debts and tax, and distributing with proper accounts." },
  { t: "Estate disputes", d: "Family provision claims and will challenges, for applicants and for executors defending an estate." },
];

const SUBURBS: { slug: string; name: string }[] = [
  { slug: "golden-beach", name: "Golden Beach" },
  { slug: "caloundra", name: "Caloundra" },
  { slug: "pelican-waters", name: "Pelican Waters" },
];

function Page() {
  return (
    <>
      <PageHero
        personSlug="michael-klein"
        eyebrow="Wills & estates · Sunshine Coast"
        title="Wills and estate lawyers on the Sunshine Coast"
        lead="Need a will on the Sunshine Coast? Solicitor-drafted wills start from $660 including GST. We also handle powers of attorney, probate and deceased estates from our Golden Beach office near Caloundra, and we visit homes and hospitals. You get the fee before we start."
        cta="Talk to a wills solicitor"
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="rounded-sm border border-border bg-card p-7">
          <h2 className="font-serif text-2xl text-foreground">Fixed fees, including GST</h2>
          <ul className="mt-5 space-y-3 text-base text-foreground">
            <li className="flex justify-between border-b border-border/60 pb-3"><span>Simple will</span><span className="font-serif text-primary">from $660</span></li>
            <li className="flex justify-between border-b border-border/60 pb-3"><span>Enduring power of attorney</span><span className="font-serif text-primary">from $440</span></li>
            <li className="flex justify-between"><span>Testamentary trust will</span><span className="font-serif text-primary">from $2,200</span></li>
          </ul>
          <p className="mt-4 text-sm text-muted-foreground">
            Probate and estate administration are quoted in writing after a short first conversation. See{" "}
            <Link to="/probate" className="font-semibold text-primary underline">probate fees</Link>.
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {SERVICES.map((s) => (
            <div key={s.t} className="rounded-sm border border-border bg-card p-6">
              <h2 className="font-serif text-xl text-foreground">{s.t}</h2>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Wills on the Sunshine Coast: what we check</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          A will deals with your estate, not necessarily everything you think of as yours. Before drafting, we check how your home is held, the arrangements for super and life insurance, and whether a blended family or business needs further planning. A joint tenant's interest generally passes to the surviving joint tenant outside the will. Read{" "}
          <Link to="/guides/$slug" params={{ slug: "joint-tenants-vs-tenants-in-common-queensland" }} className="font-semibold text-primary underline">joint tenants vs tenants in common</Link>{" "}
          before your appointment.
        </p>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Making a will when you have children</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          A will lets you name an executor and set out who should receive your estate. For parents,
          it can also record your choice of guardian for children under 18. Tell us about your family,
          any earlier relationships and who you would trust to manage an inheritance for a child.
          Guardianship and the arrangements for holding an inheritance need separate consideration;
          they are not settled simply by naming a beneficiary.
        </p>
        <h3 className="mt-8 font-serif text-2xl text-foreground">Downsizing in Caloundra or moving to the coast?</h3>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Selling a Golden Beach home and buying a unit is not just a change of address.
          Check whether a specific gift in your existing will still matches what you own,
          how the new title will be held, and whether your executor and attorney choices still suit you.
          The same questions matter when moving to Pelican Waters with a new partner or using
          sale proceeds to help an adult child. Bring the documents before making promises or transferring money.
        </p>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          If a sale or purchase is underway, our <Link to="/conveyancing-sunshine-coast" className="font-semibold text-primary underline">Sunshine Coast conveyancing solicitors</Link>
          {" "}can discuss the contract and title alongside the estate-planning questions.
          For the local property checks, see <Link to="/conveyancing/$suburb" params={{ suburb: "caloundra" }} className="font-semibold text-primary underline">buying or selling in Caloundra</Link>.
          {" "}For a blended family or a beneficiary needing support, ask whether
          <Link to="/testamentary-trusts" className="font-semibold text-primary underline"> a testamentary trust will</Link>
          {" "}is appropriate; it is not needed for every family.
        </p>
        <h3 className="mt-8 font-serif text-2xl text-foreground">A will and an enduring power of attorney do different jobs</h3>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Your will deals with your estate after death. An enduring power of attorney concerns
          decisions during your lifetime, with different rules for financial and personal matters.
          Choosing an attorney deserves its own conversation: who is suitable, when their authority
          operates and what safeguards you want. Read our{" "}
          <Link to="/guides/$slug" params={{ slug: "when-to-update-your-will-queensland" }} className="font-semibold text-primary underline">will-review checklist</Link>
          {" "}and bring any existing documents so we can discuss both together.
        </p>
        <h3 className="mt-8 font-serif text-2xl text-foreground">What to bring to a wills appointment</h3>
        <ul className="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-muted-foreground">
          <li>Your existing will and enduring power of attorney, if you have them.</li>
          <li>The names of your proposed executors, beneficiaries and any guardians.</li>
          <li>A summary of your property, superannuation, insurance, business and trust interests.</li>
          <li>Any concerns about a blended family, a vulnerable beneficiary or a possible dispute.</li>
        </ul>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Buying a home, having a child or changing a relationship is a reason to review an existing
          plan. Read <Link to="/guides/$slug" params={{ slug: "when-to-update-your-will-queensland" }} className="font-semibold text-primary underline">when to update your will</Link>,
          or <a href="#wills-enquiry" className="font-semibold text-primary underline">ask about an appointment and a written fee</a>.
        </p>
        <p className="mt-4 text-sm text-muted-foreground">
          Further reading: <a href="https://www.qld.gov.au/family/family-and-parents/legal/wills-and-estate-planning" className="text-primary underline">Queensland Government wills and estate planning guidance</a> and{" "}
          <a href="https://www.pt.qld.gov.au/wills-powers-of-attorney-estates/wills/about-wills" className="text-primary underline">Queensland Public Trustee guidance on wills</a>.
        </p>

        <h2 className="mt-16 font-serif text-3xl text-foreground">If someone has died: probate or estate administration?</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Being an executor is a legal job, and you can be personally liable if it goes wrong. Start with our{" "}
          <Link to="/probate-checklist-queensland" className="font-semibold text-primary underline">executor probate checklist</Link>,
          read{" "}
          <Link to="/guides/$slug" params={{ slug: "do-i-need-probate-in-queensland" }} className="font-semibold text-primary underline">whether you need probate</Link>,
          or{" "}
          <Link to="/probate-enquiry" className="font-semibold text-primary underline">send us the details</Link>{" "}
          and we'll tell you what's needed and what it will cost.
        </p>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Not sure where to begin? Our{" "}
          <Link to="/guides/$slug" params={{ slug: "how-to-apply-for-probate-in-queensland" }} className="font-semibold text-primary underline">Queensland probate application guide</Link>
          {" "}covers documents, notices, common hold-ups and local hypothetical examples.
          Tell us whether you want help obtaining the grant, administering the estate afterwards, or both.
        </p>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Local to you</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Looking for a wills lawyer close to home? Michael Klein sees clients at our Golden Beach
          office, serving Caloundra, Pelican Waters and the wider Sunshine Coast. Start by telling
          us whether you need your first will or a review, what has changed and whether a visit
          would suit you. We discuss the scope and fee before drafting; complex family, trust or
          business arrangements may need more than a standard will.
        </p>
        <h3 className="mt-6 font-serif text-2xl text-foreground">Three local examples — hypothetical only</h3>
        <p className="mt-3 text-sm text-muted-foreground">These are invented illustrations, not client stories or advice about a particular family.</p>
        <ul className="mt-4 list-disc space-y-4 pl-5 text-base leading-relaxed text-muted-foreground">
          <li><strong>Golden Beach downsizers:</strong> a couple sells their long-held home and buys a smaller unit. It is a good time to check the new title, existing wills and nominated attorneys together, rather than assuming all three documents do the same job.</li>
          <li><strong>Caloundra parents:</strong> parents making their first wills want to nominate a guardian and arrange an inheritance for young children. The choice of executor, guardianship and management of a child's inheritance each need consideration.</li>
          <li><strong>Pelican Waters blended family:</strong> an owner wants their partner to remain in the home and their children eventually to benefit. The title, intended living arrangements and estate plan need to be considered together; a simple gift of the house may not achieve both wishes.</li>
        </ul>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Our office is at {FIRM_ADDRESS}. We see clients at the office, at home, in hospital or in a retirement village, and by phone or video.
        </p>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Looking for a <Link to="/wills/$suburb" params={{ suburb: "caloundra" }} className="font-semibold text-primary underline">Caloundra wills lawyer</Link>?
          Tell us whether you need a first will, an update or an enduring power of attorney,
          and whether an office appointment or a visit would suit you. If someone has already
          died, our <Link to="/probate" className="font-semibold text-primary underline">Sunshine Coast probate and deceased estate administration service</Link> is the relevant starting point.
        </p>
        <ul className="mt-5 flex flex-wrap gap-2">
          {SUBURBS.map((l) => (
            <li key={`w-${l.slug}`}>
              <Link to="/wills/$suburb" params={{ suburb: l.slug }} className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:border-primary">
                Wills {l.name}
              </Link>
            </li>
          ))}
          {SUBURBS.map((l) => (
            <li key={`p-${l.slug}`}>
              <Link to="/probate/$suburb" params={{ suburb: l.slug }} className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:border-primary">
                Probate {l.name}
              </Link>
            </li>
          ))}
        </ul>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who does the work" />

        <h2 className="mt-16 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <p className="mt-10 text-sm text-muted-foreground">
          More detail:{" "}
          <Link to="/fixed-price-will-solicitor" className="font-semibold text-primary underline">fixed-price wills</Link>,{" "}
          <Link to="/home-hospital-wills-sunshine-coast" className="font-semibold text-primary underline">home and hospital wills</Link>,{" "}
          <Link to="/testamentary-trusts" className="font-semibold text-primary underline">testamentary trusts</Link>,{" "}
          <Link to="/estate-disputes" className="font-semibold text-primary underline">estate disputes</Link>,{" "}
          <Link to="/wills-and-estates" className="font-semibold text-primary underline">all wills and estates services</Link>.
        </p>
        <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
          General information only, not legal advice.
        </p>

        <div id="wills-enquiry" className="mt-14 scroll-mt-24">
          <EnquiryForm heading="Talk to us about a will or estate" defaultMatterType="wills-estates" />
        </div>
        <ServiceReading service="wills" />
      </section>

      <CtaBand title="Get your will or estate sorted" body={`Call ${FIRM.phone}. We'll confirm the fixed fee before we start.`} />
      <div className="h-20" />
    </>
  );
}
